import { useEffect, useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useOptimizerStore } from "../stores/optimizerStore";
import { deleteOptimizerJob } from "../services/optimizerService";
import OptimizerJobCard from "../components/optimizer/OptimizerJobCard";
import { PageHeader } from "../components/PageHeader";

export default function OptimizerJobsPage() {
  const {
    jobs,
    totalJobs,
    currentPage,
    statusFilter,
    isLoadingJobs,
    fetchJobs,
    fetchRunningCount,
    setStatusFilter,
    setPage,
    stopAllPolling,
  } = useOptimizerStore();

  const [searchParams, setSearchParams] = useSearchParams();
  const [showSubmittedBanner, setShowSubmittedBanner] = useState(
    searchParams.get("submitted") === "1",
  );

  // Clear the query param without re-rendering
  useEffect(() => {
    if (searchParams.get("submitted") === "1") {
      searchParams.delete("submitted");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Initial load
  useEffect(() => {
    fetchJobs();
    fetchRunningCount();
    return () => stopAllPolling();
  }, [fetchJobs, fetchRunningCount, stopAllPolling]);

  // Poll for running jobs badge count every 30s
  useEffect(() => {
    const interval = setInterval(() => fetchRunningCount(), 30000);
    return () => clearInterval(interval);
  }, [fetchRunningCount]);

  // Reload on filter change
  useEffect(() => {
    fetchJobs({ status: statusFilter, page: currentPage });
  }, [statusFilter, currentPage, fetchJobs]);

  const handleDelete = useCallback(
    async (jobId: string) => {
      if (!confirm("Delete this optimization job?")) return;
      try {
        await deleteOptimizerJob(jobId);
        fetchJobs(); // Refresh list
        fetchRunningCount();
      } catch (err) {
        console.error("Failed to delete job:", err);
      }
    },
    [fetchJobs, fetchRunningCount],
  );

  // Status filter tabs
  const statusFilters: { key: string | undefined; label: string }[] = [
    { key: undefined, label: "All" },
    { key: "running", label: "Running" },
    { key: "completed", label: "Completed" },
    { key: "failed", label: "Failed" },
  ];

  // Count by status (approximate from current page)
  const runningCount = jobs.filter((j) => j.status === "running").length;

  return (
    <div>
      <PageHeader
        title="Strategy Optimizer"
        subtitle="Evolve and improve your trading strategies with AI"
        backgroundImage="https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=1920&q=80"
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* ── How it works ───────────────────────────── */}
      <div className="mb-6 rounded-lg bg-surface-800/40 border border-surface-700/50 px-4 py-3">
        <p className="text-sm text-surface-400 leading-relaxed">
          <span className="text-surface-200 font-medium">How it works:</span>{" "}
          Pick any strategy → set a target metric (higher CAGR, better Sharpe, lower drawdown) → submit.
          The engine uses <span className="text-primary-400 font-medium">NSGA-II genetic programming</span> to
          evolve hundreds of strategy variants — mutating indicator windows, swapping subtrees, injecting crash
          guards and trend filters. Each variant is backtested over 10 years, then validated with a 70/30
          in-sample / out-of-sample split to flag overfitting. Top candidates are ranked and returned with
          full metrics. Typical run: 5–10 minutes.
        </p>
      </div>

      {/* Submitted Banner */}
      {showSubmittedBanner && (
        <div className="mb-6 p-4 rounded-xl bg-blue-500/10 border border-blue-500/30 relative">
          <button
            onClick={() => setShowSubmittedBanner(false)}
            className="absolute top-3 right-3 text-surface-400 hover:text-surface-200"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="flex gap-3">
            <div className="text-2xl">🧬</div>
            <div>
              <h3 className="text-sm font-semibold text-blue-400 mb-1">
                Optimization Submitted
              </h3>
              <p className="text-sm text-surface-300">
                Strategy optimization typically takes <strong className="text-surface-100">5–10 minutes</strong>.
                The engine evolves hundreds of strategy variants using genetic programming, backtests each one,
                and filters for candidates that improve on your target metric while preserving constraints.
              </p>
              <p className="text-sm text-surface-400 mt-1">
                Your job will appear below as <span className="text-blue-400 font-medium">Running</span> and
                move to <span className="text-green-400 font-medium">Completed</span> when finished. You can
                leave this page and come back anytime.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Status Filter Tabs */}
      <div className="flex gap-2 mb-6">
        {statusFilters.map(({ key, label }) => (
          <button
            key={label}
            onClick={() => setStatusFilter(key)}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
              statusFilter === key
                ? "bg-primary-500/20 text-primary-400 border border-primary-500/30"
                : "text-surface-400 hover:text-surface-200 hover:bg-surface-800/50"
            }`}
          >
            {label}
            {key === "running" && runningCount > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-blue-500/20 text-blue-400 rounded-full">
                {runningCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Jobs List */}
      {isLoadingJobs ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-400"></div>
        </div>
      ) : jobs.length === 0 ? (
        <div className="text-center py-16 text-surface-400">
          <svg
            className="w-12 h-12 mx-auto mb-4 text-surface-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
            />
          </svg>
          <p className="font-medium text-surface-300 mb-1">
            No optimization jobs yet
          </p>
          <p className="text-sm">
            Try improving a strategy from the Backtest page or Browse page.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {jobs.map((job) => (
            <OptimizerJobCard
              key={job.job_id}
              job={job}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalJobs > 20 && (
        <div className="flex justify-center gap-2 mt-8">
          <button
            onClick={() => setPage(currentPage - 1)}
            disabled={currentPage <= 1}
            className="px-3 py-1.5 text-sm text-surface-400 disabled:opacity-30"
          >
            Previous
          </button>
          <span className="px-3 py-1.5 text-sm text-surface-300">
            Page {currentPage} of {Math.ceil(totalJobs / 20)}
          </span>
          <button
            onClick={() => setPage(currentPage + 1)}
            disabled={currentPage * 20 >= totalJobs}
            className="px-3 py-1.5 text-sm text-surface-400 disabled:opacity-30"
          >
            Next
          </button>
        </div>
      )}
      </div>
    </div>
  );
}
